// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import moment from 'moment';
import React from 'react';
import RedBar from '../components/RedBar';
import GatsbyBackgroundImage from '../components/images/GatsbyBackgroundImage';

import './BlogCloud.scss';

export const BlogCloud_REFERENCES = ['blog_cloud.posts.background_image'];
export const query = graphql`
	fragment BlogCloudFragment on Ozk_pageContent {
		blog_cloud {
			heading
			include_red_line
			posts {
				author
				date
				headline
				background_image {
					...BynderImageFragment
				}
				link {
					title
					href
				}
				size
			}
		}
	}
`;

/**
 * @typedef SingleBlog
 * @property {string} author The author of the post.
 * @property {string} date The formatted date of the post.
 * @property {string} headline The primary headline of the post.
 * @property {import('../CSTypes').BynderImageOptions} background_image The Bynder image for the post background.
 * @property {import('../CSTypes').CSLink} link The destination of clicking on the post.
 * @property {'1x1' | '2x2'} size The optional size for the blogs. Only guaranteed for one 2x2, then four 1x1s.
 *
 * @typedef BlogCloudOptions
 * @property {string} heading
 * @property {boolean} include_red_line
 * @property {SingleBlog[]} posts
 *
 * @typedef BlogCloudProps
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<BlogCloudOptions>} [options]
 */
/** @type {import('~/types/React').ReactFC<BlogCloudProps>} */
const BlogCloud = ({ options, ...rest }) => {
	if (!options) {
		return null;
	}

	return (
		<div {...rest} className={classNames(rest.className, 'blog-cloud container')}>
			{options.include_red_line && <RedBar />}
			<h3 className="heading">{options.heading}</h3>

			<div className="blog-cloud-post-container">
				{options.posts.map((post, i) => (
					<GatsbyBackgroundImage
						key={i}
						className={classNames('blog-post', post.size === '2x2' ? 'big-box' : 'small-box')}
						option={post.background_image?.[0]}
					>
						<a className="link" title={post.link.title} href={post.link.href}>
							<p className="date-authored">{moment(post.date).format('MMMM D, YYYY')}</p>
							<h2 className="headline">{post.headline}</h2>
							<p className="author">{post.author}</p>
						</a>
					</GatsbyBackgroundImage>
				))}
			</div>
		</div>
	);
};

export default BlogCloud;
